import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { PropertyDto } from 'src/app/core/services/property-definitions-api.service';
import { findPropertyItems } from '../utils/property-utils';
import {
  CommentDto,
  CommentResponse,
  CommentReviewStatusRequest,
  HideCommentRequest,
} from './comment-list/comment/comment.model';
import { CommentsPanelSelection, IdPair } from './comments-panel-selection';

@Injectable({
  providedIn: 'root',
})
export class CommentsPanelService {
  readonly #selectedComment$ = new Subject<UNDEFINED<CommentsPanelSelection>>();
  readonly #hideComment$ = new Subject<HideCommentRequest>();
  readonly #openComment$ = new Subject<CommentDto>();
  readonly #showRelated$ = new Subject<CommentDto>();
  readonly #addComment$ = new Subject<string>();
  readonly #deleteComment$ = new Subject<number>();
  readonly #editComment$ = new Subject<CommentResponse>();
  readonly #addResponse$ = new Subject<CommentResponse>();
  readonly #reviewStatus$ = new Subject<CommentReviewStatusRequest>();
  readonly #cachedSelection = new Map<string, string>();
  readonly #commentTabChanged$ = new Subject<number>();
  readonly scrollPosition = signal(0);

  onShowRelated$ = this.#showRelated$.asObservable();
  onAddComment$ = this.#addComment$.asObservable();
  onDeleteComment$ = this.#deleteComment$.asObservable();
  onEditComment$ = this.#editComment$.asObservable();
  onAddResponse$ = this.#addResponse$.asObservable();
  onReviewStatusChanged$ = this.#reviewStatus$.asObservable();
  onCommentTabChanged$ = this.#commentTabChanged$.asObservable();
  readonly #propertyItemsStore$ = new BehaviorSubject<PropertyDto[]>([]);
  propertyItemsStore$ = this.#propertyItemsStore$.asObservable();

  getSelectedComment(): Observable<UNDEFINED<CommentsPanelSelection>> {
    return this.#selectedComment$.asObservable();
  }

  setSelectedComment(value: UNDEFINED<CommentsPanelSelection>) {
    this.#selectedComment$.next(value);
  }

  set setCommentVisibility(data: HideCommentRequest) {
    this.#hideComment$.next(data);
  }

  get getCommentVisibility$(): Observable<HideCommentRequest> {
    return this.#hideComment$.asObservable();
  }

  openComment(comment: CommentDto) {
    this.#openComment$.next(comment);
  }

  showRelated(comment: CommentDto) {
    this.#showRelated$.next(comment);
  }

  addComment(text: string) {
    this.#addComment$.next(text);
  }

  deleteComment(commentId: number) {
    this.#deleteComment$.next(commentId);
  }

  editComment(commentResponse: CommentResponse) {
    this.#editComment$.next(commentResponse);
  }

  addResponse(commentResponse: CommentResponse) {
    this.#addResponse$.next(commentResponse);
  }

  /* Send comment review status for API update */
  setCommentReviewStatus(reviewStatusData: CommentReviewStatusRequest): void {
    this.#reviewStatus$.next(reviewStatusData);
  }

  /* Store interface items properties */
  set setPropertyItems(propertyItems: PropertyDto[]) {
    this.#propertyItemsStore$.next(propertyItems);
  }

  getPropertyItems(items: Partial<IdPair>[] = []): PropertyDto[] {
    return findPropertyItems(this.#propertyItemsStore$.value, items);
  }

  /* set comment selection cache **/
  set cachedSelection(selection: { properties: IdPair[]; value: string }) {
    selection.properties?.forEach(({ propertyDefId, registerItemId }) => {
      const key = this.#cachedSelectionKey({ propertyDefId, registerItemId });
      this.#cachedSelection.set(key, selection.value);
    });
  }

  /* create unique cached comment key **/
  #cachedSelectionKey({ propertyDefId, registerItemId }): `${number} ${number}` {
    return `${propertyDefId} ${registerItemId}`;
  }

  /* get the cached comment **/
  getCachedComment(properties?: IdPair[]): string | undefined {
    if (!properties) {
      return;
    }

    // Get all comments in the cached Object
    const textComments = properties.map(({ registerItemId, propertyDefId }) => {
      const cacheKey = this.#cachedSelectionKey({ propertyDefId, registerItemId });
      return this.#cachedSelection.get(cacheKey);
    });

    // check if all comments are the same and ignore undefined or empty comments
    const canReturnComment = textComments.every(text => {
      return textComments.every(compairedText => {
        return !compairedText || !text || text === compairedText;
      });
    });

    // get the common comment if exist
    const commentText = textComments.find(comment => !!comment);
    return canReturnComment ? commentText : undefined;
  }

  /* delete specific cached comment **/
  deleteCachedSelection(properties?: IdPair[]): void {
    properties?.forEach(({ propertyDefId, registerItemId }) => {
      const key = this.#cachedSelectionKey({ propertyDefId, registerItemId });
      this.#cachedSelection.delete(key);
    });
  }

  commentTabChanged(tabIndex: number) {
    this.#commentTabChanged$.next(tabIndex);
  }

  /* clear all cached comment data **/
  clearCachedSelection(): void {
    this.#cachedSelection.clear();
  }
}
